import { apiClient } from '../../api';
import { logError } from '../../utils/logger';
import type { ActionKey } from './contracts';

/**
 * The frontend-owned action allowlist (design-spec §5, task requirement 5).
 * The server names an `actionKey`; this table — never the SSE event — decides
 * which real endpoint gets called. A model that emits an `actionKey` not in
 * this map is a rendering error, never a request to an arbitrary URL: the AI
 * never writes, it can only ever get as far as naming one of these four keys.
 *
 * `publishService` and `saveServiceDraft` are provider-persona actions
 * (design-spec §9's ServiceDraftEditor) — this is the customer portal, so
 * they resolve to `null` (rendering error) here on purpose rather than being
 * wired to an endpoint that doesn't apply to this persona.
 */
const PROWL_ACTION_ENDPOINTS: Partial<Record<ActionKey, string>> = {
  createBooking: '/api/v1/booking',
  createQuoteRequest: '/api/v1/quote-request',
};

export const isKnownProwlAction = (key: string): key is ActionKey =>
  Object.prototype.hasOwnProperty.call(PROWL_ACTION_ENDPOINTS, key);

export interface ExecuteProwlActionResult {
  ok: boolean;
  data?: unknown;
  error?: string;
}

/**
 * Executes a resolved action: the frontend calls the real REST endpoint with
 * the user's own session (apiClient's cookie auth) and the existing
 * server-side validation — no new write path, no new authorization surface
 * (design-spec §5).
 */
export const executeProwlAction = async (
  actionKey: string,
  payload: unknown
): Promise<ExecuteProwlActionResult> => {
  if (!isKnownProwlAction(actionKey)) {
    logError('[prowl] unknown actionKey — refusing to construct a request', {
      actionKey,
    });
    return { ok: false, error: 'unknown_action' };
  }

  const endpoint = PROWL_ACTION_ENDPOINTS[actionKey];
  if (!endpoint) {
    logError('[prowl] actionKey not wired for this persona', { actionKey });
    return { ok: false, error: 'unsupported_action' };
  }

  try {
    const res = await apiClient.post(endpoint, payload);
    return { ok: true, data: res.data };
  } catch (error: any) {
    logError(`[prowl] action "${actionKey}" failed`, {
      message: error?.message,
    });
    return { ok: false, error: error?.message || 'request_failed' };
  }
};
